import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useMemo } from 'react';
import { BrokerPortfolioTypesEnum } from 'dash-common';
import {
  Box,
  Devider,
  Flex,
  Text,
  useScreenResolution,
  Button,
  Spinner,
  ExpandableText,
} from 'dash-ui';
import {
  PortfolioFragment,
  useGetPortfolioFolderItemsQuery,
} from 'gql/generated.graphql';
import PortfolioActionsDropDown from 'pages/portfolios/PortfoliosPage/components/PortfoliosPageView/components/PortfolioActionsDropDown/PortfolioActionsDropDown';

import { StyledPortfolioWrapper } from './PortfolioCardItem.styles';
import PortfolioThumbnailView from './components/PortfolioThumbnailView/PortfolioThumbnailView';
import PortfolioActionButtons from './components/PortfolioActionButtons/PortfolioActionButtons';
import PortfolioStatusLabel from './components/PortfolioStatusLabel/PortfolioStatusLabel';

const PortfolioCardItem = ({
  portfolio,
  onClick,
}: {
  portfolio: PortfolioFragment;
  onClick: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation('pages');
  const {
    devices: { isTablet, isMobile },
  } = useScreenResolution();
  const isFolder = portfolio.type === BrokerPortfolioTypesEnum.FOLDER;
  const { data, loading } = useGetPortfolioFolderItemsQuery({
    variables: {
      portfolioFolderId: portfolio._id,
    },
    skip: !isFolder,
  });

  const { properties, totalCount } = useMemo(
    () => ({
      properties: data?.getPortfolioFolderItems.collection || [],
      totalCount: data?.getPortfolioFolderItems.totalCount || 0,
    }),
    [data]
  );

  return (
    <StyledPortfolioWrapper w="100%" onClick={onClick} data-testid="portfolio-card">
      {isTablet || isMobile ? (
        <PortfolioActionsDropDown
          portfolio={portfolio}
          Anchor={() => <Button square icon="dots" />}
        />
      ) : (
        <PortfolioActionButtons portfolio={portfolio} />
      )}
      <PortfolioThumbnailView
        properties={properties}
        portfolio={portfolio}
        totalCount={portfolio.portfolioPropertiesIds?.length || 0}
        isFolder={isFolder}
      />
      <Box p={4} h={102}>
        <ExpandableText
          withButton={false}
          lineClamp={2}
          semibold
          lg={!isTablet && !isMobile}
        >
          {portfolio.name}
        </ExpandableText>
        <Text color={theme.colors.n80}>
          {isFolder
            ? t('portfoliosPage.propertiesTotal', {
                total: portfolio.portfolioPropertiesIds?.length || 0,
              })
            : `${portfolio.property?.location.address?.city}, ${portfolio.property?.location.address?.county}`}
        </Text>
      </Box>
      <Devider w="100%" color={theme.colors.n30} />
      <Flex alignItems="center" pt={3} pb={5}>
        <PortfolioStatusLabel portfolio={portfolio} />

        <Flex justifyContent="center" flex={1}>
          <Text color={theme.colors.b50}>
            {t('portfoliosPage.totalViews', { total: portfolio.viewsCount })}
          </Text>
        </Flex>
      </Flex>
      {loading && (
        <Spinner
          size={32}
          fullContent
          ignoreResponsive
          bgColor={theme.colors.n20}
        />
      )}
    </StyledPortfolioWrapper>
  );
};

export default PortfolioCardItem;
